if (!customElements.get("td-tabs")) {
  class TdTabs extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.TABS_WRAPPER = this.querySelector('[data-tabs-wrapper]')
      this.TRIGGERS = this.querySelectorAll('[data-id]');
      this.TAB_SCROLL_LEFT = this.querySelector('[data-scroll-tab-left]');
      this.TAB_SCROLL_RIGHT = this.querySelector('[data-scroll-tab-right]');
      this.ANIMATED_UNDERLINE = this.querySelector('[data-animated-underline]');
      this.activeTab = this.querySelector('.__tabs-tab.td-active');
      this.activeTabContent = document.querySelector(`#${this.activeTab?.dataset?.id}`);
      if (!this.ANIMATED_UNDERLINE || !this.activeTab || !this.activeTabContent) {
        return
      }

      this.setActiveTab();
      this.setActiveTabNavArrows();
      this.attachListeners();
    }

    attachListeners() {
      this.TRIGGERS.forEach((trigger) => {
        // Handle mouse clicks
        trigger.addEventListener('click', () => {
          this.activeTab = trigger;
          this.setActiveTab();
        });

        // Handle keyboard activation (Enter/Space)
        trigger.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.activeTab = trigger;
            this.setActiveTab();
          }
        });
      });
      this.TABS_WRAPPER.addEventListener('scroll', () => {
        this.setActiveTabNavArrows();
      })
      this.TAB_SCROLL_LEFT.addEventListener('click', () => {
        this.scrollTabs(-100, 'left');
      })
      this.TAB_SCROLL_RIGHT.addEventListener('click', () => {
        this.scrollTabs(100, 'right');
      })
      window.addEventListener('resize', () => {
        this.setActiveTab();
        this.setActiveTabNavArrows();
      })
      window.addEventListener('td:section-switcher-changed', () => {
        this.setActiveTab();
      })
    }

    setActiveTab() {
      this.activeTabContent.classList.remove('td-active');
      this.activeTabContent = document.querySelector(`#${this.activeTab?.dataset?.id}`);

      // Update aria-selected BEFORE measuring offsetWidth — the active tab uses
      // font-weight: 700 via the [aria-selected="true"] CSS rule, which makes
      // the text wider. Measuring before the attribute flips would size the
      // underline to the lighter, narrower width (visible misalignment).
      this.TRIGGERS.forEach(trigger => {
        trigger.setAttribute('aria-selected', 'false');
      });
      this.activeTab.setAttribute('aria-selected', 'true');

      this.ANIMATED_UNDERLINE.style.cssText = `
        left: ${this.activeTab.offsetLeft}px;
        width: ${this.activeTab.offsetWidth - 0.5}px;
      `;
      this.activeTabContent.classList.add('td-active');

      // Announce tab change to screen readers
      const announcement = document.querySelector('[data-tab-announcement]');
      if (announcement) {
        const tabName = this.activeTab.textContent.trim() || 'tab';
        announcement.innerHTML = `${tabName} tab selected`;
      }
    }

    setActiveTabNavArrows() {
      if (!this.TAB_SCROLL_LEFT || !this.TAB_SCROLL_RIGHT) {
        return
      }
      if (this.isScrollable()) {
        if (this.isScrolledLeft()) {
          this.TAB_SCROLL_LEFT.classList.remove('td-active');
        } else {
          this.TAB_SCROLL_LEFT.classList.add('td-active');
        }
        
        if (this.isScrolledRight()) {
          this.TAB_SCROLL_RIGHT.classList.remove('td-active');
        } else {
          this.TAB_SCROLL_RIGHT.classList.add('td-active');
        }
      } else {
        this.TAB_SCROLL_LEFT.classList.remove('td-active');
        this.TAB_SCROLL_RIGHT.classList.remove('td-active');
      }
    }

    isScrollable() {
      return this.TABS_WRAPPER.scrollWidth > this.TABS_WRAPPER.clientWidth;
    }

    isScrolledLeft() {
      return this.TABS_WRAPPER.scrollLeft === 0;
    }

    isScrolledRight() {
      const tolerance = 1;
      return Math.abs(this.TABS_WRAPPER.scrollLeft + this.TABS_WRAPPER.clientWidth - this.TABS_WRAPPER.scrollWidth) <= tolerance;
    }

    scrollTabs(scrollAmount, direction) {
      const currentScrollPosition = this.TABS_WRAPPER.scrollLeft + scrollAmount;
      if (currentScrollPosition <= 0) {
        this.TABS_WRAPPER.scrollTo({
          left: 0,
          behavior: 'smooth'
        });
      } else if (currentScrollPosition >= (this.TABS_WRAPPER.scrollWidth - this.TABS_WRAPPER.clientWidth)) {
        this.TABS_WRAPPER.scrollTo({
          left: this.TABS_WRAPPER.scrollWidth - this.TABS_WRAPPER.scrollLeft,
          behavior: 'smooth'
        });
      } else {
        this.TABS_WRAPPER.scrollTo({
          left: currentScrollPosition,
          behavior: 'smooth'
        });
      }
    }
  }

  customElements.define("td-tabs", TdTabs);
}

if (!customElements.get("td-reels-trigger-scroll")) {
  class TdReelsTriggerScroll extends HTMLElement {
    connectedCallback() {
      this.reelsWrapper = this.querySelector('[data-reels-wrapper]');
      this.scrollLeftBtn = this.querySelector('[data-scroll-reels-left]');
      this.scrollRightBtn = this.querySelector('[data-scroll-reels-right]');

      if (!this.reelsWrapper || !this.scrollLeftBtn || !this.scrollRightBtn) {
        return;
      }

      this.boundUpdateArrows = () => this.updateArrows();
      this.boundScrollLeft = () => this.scrollByDelta(-100);
      this.boundScrollRight = () => this.scrollByDelta(100);

      this.reelsWrapper.addEventListener('scroll', this.boundUpdateArrows);
      this.scrollLeftBtn.addEventListener('click', this.boundScrollLeft);
      this.scrollRightBtn.addEventListener('click', this.boundScrollRight);
      window.addEventListener('resize', this.boundUpdateArrows);

      this.resizeObserver = new ResizeObserver(this.boundUpdateArrows);
      this.resizeObserver.observe(this.reelsWrapper);

      this.updateArrows();
    }

    disconnectedCallback() {
      if (!this.reelsWrapper) return;
      this.reelsWrapper.removeEventListener('scroll', this.boundUpdateArrows);
      this.scrollLeftBtn.removeEventListener('click', this.boundScrollLeft);
      this.scrollRightBtn.removeEventListener('click', this.boundScrollRight);
      window.removeEventListener('resize', this.boundUpdateArrows);
      this.resizeObserver?.disconnect();
    }

    updateArrows() {
      if (!this.isScrollable()) {
        this.scrollLeftBtn.classList.remove('td-active');
        this.scrollRightBtn.classList.remove('td-active');
        return;
      }
      this.scrollLeftBtn.classList.toggle('td-active', !this.isAtStart());
      this.scrollRightBtn.classList.toggle('td-active', !this.isAtEnd());
    }

    isScrollable() {
      return this.reelsWrapper.scrollWidth > this.reelsWrapper.clientWidth;
    }

    isAtStart() {
      return this.reelsWrapper.scrollLeft === 0;
    }

    isAtEnd() {
      const tolerance = 1;
      const maxScroll = this.reelsWrapper.scrollWidth - this.reelsWrapper.clientWidth;
      return Math.abs(this.reelsWrapper.scrollLeft - maxScroll) <= tolerance;
    }

    scrollByDelta(delta) {
      const max = this.reelsWrapper.scrollWidth - this.reelsWrapper.clientWidth;
      const target = Math.max(0, Math.min(this.reelsWrapper.scrollLeft + delta, max));
      this.reelsWrapper.scrollTo({ left: target, behavior: 'smooth' });
    }
  }

  customElements.define("td-reels-trigger-scroll", TdReelsTriggerScroll);
}

